import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useWebsiteInputs } from '../../utils/hooks/useWebsiteInputs';
import { getImageUrl } from '../../utils/supabaseStorage';

// Pre-defined arrays with different orders
const schoolData = {
    Stanford: {
      logo: getImageUrl('colleges/stanford_image.png')
    },
    Columbia: {
      logo: getImageUrl('colleges/columbia_image.gif')
    },
    UChicago: {
      logo: getImageUrl('colleges/uchicago_image.png')
    },
    "WashU St. Louis": {
      logo: getImageUrl('colleges/washu-st-louis_image.png')
    },
    "UT-Austin": {
      logo: getImageUrl('colleges/texas_image.png')
    },
    "Johns Hopkins": {
      logo: getImageUrl('colleges/johns-hopkins_image.svg')
    },
    Maryland: {
      logo: getImageUrl('colleges/maryland_image.png')
    },
    Berkeley: {
      logo: getImageUrl('colleges/berkeley_image.png')
    },
    Virginia: {
      logo: getImageUrl('colleges/virginia_image.png')
    },
    MIT: {
      logo: getImageUrl('colleges/mit_image.svg')
    },
    NYU: {
      logo: getImageUrl('colleges/nyu_image.png')
    },
    UCLA: {
      logo: getImageUrl('colleges/ucla_image.png')
    },
    Illinois: {
      logo: getImageUrl('colleges/illinois_image.png')
    },
    Oxford: {
      logo: getImageUrl('colleges/oxford_image.png')
    },
    Dartmouth: {
      logo: getImageUrl('colleges/dartmouth_image.jpg')
    },
    Emory: {
      logo: getImageUrl('colleges/emory_image.png')
    },
    Harvard: {
      logo: getImageUrl('colleges/harvard_image.png?v=1788904752440')
    },
    Yale: {
      logo: getImageUrl('colleges/yale_image.png')
    },
    Penn: {
      logo: getImageUrl('colleges/penn_image.svg')
    },
    Cornell: {
      logo: getImageUrl('colleges/cornell_image.svg')
    },
    Duke: {
      logo: getImageUrl('colleges/duke_image.jpg?v=1789324456966')
    },
    Caltech: {
      logo: getImageUrl('colleges/caltech_image.jpg')
    },
    Michigan: {
      logo: getImageUrl('colleges/michigan_image.svg')
    },
    "North Carolina": {
      logo: getImageUrl('colleges/north-carolina_image.svg')
    },
    USC: {
      logo: getImageUrl('colleges/usc_image.png')
    },
    Rice: {
      logo: getImageUrl('colleges/rice_image.png')
    },
    "Carnegie Mellon": {
      logo: getImageUrl('colleges/carnegie-mellon_image.jpg?v=1781717955132')
    },
    "Georgia Tech": {
      logo: getImageUrl('colleges/georgia-tech_image.png')
    },
    Northwestern: {
      logo: getImageUrl('colleges/northwestern_image.svg')
    },
    Vanderbilt: {
      logo: getImageUrl('colleges/vanderbilt_image.svg')
    },
    Georgetown: {
      logo: getImageUrl('colleges/georgetown_image.png')
    },
    Washington: {
      logo: getImageUrl('colleges/washington_image.svg')
    },
    Wisconsin: {
      logo: getImageUrl('colleges/wisconsin_image.svg')
    },
    Purdue: {
      logo: getImageUrl('colleges/purdue_image.png')
    },
    UCL: {
      logo: getImageUrl('colleges/ucl_image.png?v=1781665238839'),
      invert: true // black-on-transparent logo; flip to white on the dark background
    },
    Waterloo: {
      logo: getImageUrl('colleges/waterloo_image.png?v=1781665230615')
    },
    Syracuse: {
      logo: getImageUrl('colleges/syracuse_image.png')
    },
    Rochester: {
      logo: getImageUrl('colleges/rochester_image.jpg?v=1788904817946')
    },
    UMass: {
      logo: getImageUrl('colleges/umass_image.svg')
    },
    "Santa Clara": {
      logo: getImageUrl('colleges/santa-clara_image.png')
    },
    "UC Santa Cruz": {
      logo: getImageUrl('colleges/uc-santa-cruz_image.png?v=1781665256203')
    },
    "Claremont McKenna": {
      logo: getImageUrl('colleges/claremont-mckenna_image.png?v=1781717898258')
    },
    NUS: {
      logo: getImageUrl('colleges/national-university-of-singapore_image.jpg?v=1788904898170')
    }
  };
  
  const labelArrays = {
    first: [
    "Stanford",
    "UChicago",
    "WashU St. Louis",
    "UT-Austin",
    "Virginia",
    "MIT",
    "Illinois",
    "Johns Hopkins",
    "Dartmouth",
    "Harvard",
    "Yale",
    "Penn",
    "Cornell",
    "Duke",
    "Caltech"
    ],
    second: [
    "UCLA",
    "Columbia",
    "Dartmouth",
    "Maryland",
    "Oxford",
    "NYU",
    "Stanford",
    "Emory",
    "Berkeley",
    "Michigan",
    "North Carolina",
    "USC",
    "Rice",
    "Carnegie Mellon",
    "Georgia Tech",
    "Northwestern"
    ],
    third: [
    "Vanderbilt",
    "Georgetown",
    "Washington",
    "Wisconsin",
    "Purdue",
    "UCL",
    "Waterloo",
    "Syracuse",
    "Rochester",
    "UMass",
    "Santa Clara",
    "UC Santa Cruz",
    "Claremont McKenna",
    "NUS"
    ]
  };
  
  const LabelScroll = ({ speed = '30s', reverse = false, variant = 'first' }) => {
    const labels = labelArrays[variant];
    const totalLabels = labels.length;
  
    return (
      <div className="logo-loop">
        <div 
          className="logo-track"
          style={{
            '--total-items': totalLabels,
            '--animation-duration': speed,
            'flexDirection': reverse ? 'row-reverse' : 'row'
          }}
        >
          {[...labels, ...labels].map((label, index) => (
            <span 
              key={`label-${index}`}
              className="label"
            >
              {schoolData[label]?.logo && (
                <img 
                  src={schoolData[label].logo} 
                  alt={`${label} logo`}
                  className="school-logo"
                  style={schoolData[label].invert ? { filter: 'invert(1)' } : undefined}
                />
              )}
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  }; 
       

function NextBatch({ inContainer = true }) {
  const { inputs: websiteInputs } = useWebsiteInputs();
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = countRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && websiteInputs?.["Total Investments"] > 0) {
      let start = 0;
      const end = websiteInputs?.["Total Investments"] ? parseInt(websiteInputs["Total Investments"]) : 0;
      const duration = 1500;
      const increment = 1;
      const stepTime = Math.abs(Math.floor(duration / (end - start)));

      const timer = setInterval(() => {
        start += increment;
        setCount(start);

        if (start >= end) {
          clearInterval(timer);
        }
      }, stepTime);

      return () => clearInterval(timer);
    }
  }, [isVisible, websiteInputs]);

  return (
    <main>
      {inContainer && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      {inContainer && (
        <div className="container">
          <section className="content-section">
            <div className="list-grid">
              <div className="list-item crossed-out no-bullet">Accelerator</div>
              <div className="list-item bold no-bullet">First-Believer Fund</div>
            </div>
            <p>Help the ambitious start.</p>
            <p>Give them a network.</p>
            <p>Be their first believer.</p>
          </section>
          <section className="content-section">
            <p ref={countRef}><span className="counter">{count}</span> teams backed so far.</p>
            <p>90%+ applied pre-revenue.</p>
            <p>70%+ raised additional capital.</p>
            <LabelScroll speed="150s" variant="first" />
            <LabelScroll speed="160s" variant="second" />
            <LabelScroll speed="140s" variant="third" />
          </section>
        </div>
      )}
      {!inContainer && (
        <div>
          <section className="content-section">
            <div className="list-grid">
              <div className="list-item crossed-out no-bullet">Accelerator</div>
              <div className="list-item bold no-bullet">First-Believer Fund</div>
            </div>
            <p>Help the ambitious start.</p>
            <p>Give them a network.</p>
            <p>Be their first believer.</p>
          </section>
          <section className="content-section">
            <p ref={countRef}><span className="counter">{count}</span> teams backed so far.</p>
            <p>90%+ applied pre-revenue.</p>
            <p>70%+ raised additional capital.</p>
            <LabelScroll speed="150s" variant="first" />
            <LabelScroll speed="160s" variant="second" />
            <LabelScroll speed="140s" variant="third" />
          </section>
        </div>
      )}
    </main>
  );
}

export default NextBatch;